import { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Loader2, Trash2, Plus, X, Pencil } from "lucide-react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  getFeatureFlags,
  createFeatureFlag,
  deleteFeatureFlag,
  createFeatureFlagScope,
  deleteFeatureFlagScope,
} from "@/data-access/feature-flags";
import { searchUsers } from "@/data-access/users";
import type {
  GetFeatureFlagsResponseDto,
  CreateFeatureFlagDto,
  GetFeatureFlagDto,
  GetFeatureFlagScopeDto,
} from "@server/routes/feature-flags/dto";
import type { UserDto, SearchUsersResponseDto } from "@server/routes/users/dto";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

type FeatureFlag = GetFeatureFlagDto;
type FeatureFlagScope = GetFeatureFlagScopeDto;
type User = UserDto;

export default function FeatureFlagsTab() {
  const [newFlagSlug, setNewFlagSlug] = useState("");
  const [newFlagDescription, setNewFlagDescription] = useState("");
  const [showNewFlagForm, setShowNewFlagForm] = useState(false);
  const [userSearchTerms, setUserSearchTerms] = useState<Record<number, string>>({});
  const [searchResults, setSearchResults] = useState<Record<number, SearchUsersResponseDto['users']>>({});
  const [searchLoading, setSearchLoading] = useState<Record<number, boolean>>({});

  const queryClient = useQueryClient();

  // Fetch feature flags
  const { data: flagsData, isLoading: flagsLoading, error: flagsError } = useQuery({
    queryKey: ['featureFlags'],
    queryFn: getFeatureFlags,
  });

  // Mutations
  const createFlagMutation = useMutation({
    mutationFn: createFeatureFlag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featureFlags'] });
      setNewFlagSlug("");
      setNewFlagDescription("");
      setShowNewFlagForm(false);
    },
  });

  const deleteFlagMutation = useMutation({
    mutationFn: deleteFeatureFlag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featureFlags'] });
    },
  });

  const createScopeMutation = useMutation({
    mutationFn: ({ flagId, scopeType, scopeId }: { flagId: number; scopeType: string; scopeId: string }) =>
      createFeatureFlagScope(flagId, scopeType, scopeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featureFlags'] });
    },
  });

  const deleteScopeMutation = useMutation({
    mutationFn: ({ flagId, scopeType, scopeId }: { flagId: number; scopeType: string; scopeId: string }) =>
      deleteFeatureFlagScope(flagId, scopeType, scopeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featureFlags'] });
    },
  });

  // Debounced search effect
  useEffect(() => {
    const searchTimeouts: Record<number, NodeJS.Timeout> = {};

    Object.entries(userSearchTerms).forEach(([flagIdStr, searchTerm]) => {
      const flagId = parseInt(flagIdStr);
      
      if (searchTerm.trim()) {
        searchTimeouts[flagId] = setTimeout(async () => {
          setSearchLoading(prev => ({ ...prev, [flagId]: true }));
          try {
            const results = await searchUsers(searchTerm.trim());
            setSearchResults(prev => ({ ...prev, [flagId]: results.users }));
          } catch (error) {
            console.error("Error searching users:", error);
            setSearchResults(prev => ({ ...prev, [flagId]: [] }));
          } finally {
            setSearchLoading(prev => ({ ...prev, [flagId]: false }));
          }
        }, 300); // 300ms debounce
      } else {
        setSearchResults(prev => ({ ...prev, [flagId]: [] }));
        setSearchLoading(prev => ({ ...prev, [flagId]: false }));
      }
    });

    return () => {
      Object.values(searchTimeouts).forEach(timeout => clearTimeout(timeout));
    };
  }, [userSearchTerms]);

  // Helper functions
  const getScopesOfType = (flag: FeatureFlag, type: string) => {
    return flag.scopes?.filter(scope => scope.scopeType === type) || [];
  };

  const hasGlobalScope = (flag: FeatureFlag) => {
    return flag.scopes?.some(scope => scope.scopeType === "global") || false;
  };

  const getUserById = (userId: string) => {
    // First, try to find in search results across all flags
    for (const flagSearchResults of Object.values(searchResults)) {
      const user = flagSearchResults.find(user => user.id.toString() === userId);
      if (user) return user;
    }
    // If not found, return a fallback with just the user ID
    return { id: parseInt(userId), username: `User ${userId}`, email: '' };
  };

  const handleCreateFlag = () => {
    if (!newFlagSlug.trim()) return;
    
    createFlagMutation.mutate({
      slug: newFlagSlug.trim(),
      description: newFlagDescription.trim() || undefined,
    });
  };

  const handleToggleGlobal = (flag: FeatureFlag) => {
    const hasGlobal = hasGlobalScope(flag);
    
    if (hasGlobal) {
      // Remove global scope
      deleteScopeMutation.mutate({
        flagId: flag.id,
        scopeType: "global",
        scopeId: "global",
      });
    } else {
      // Add global scope
      createScopeMutation.mutate({
        flagId: flag.id,
        scopeType: "global",
        scopeId: "global",
      });
    }
  };

  const handleAddUserScope = (flagId: number, userId: string) => {
    createScopeMutation.mutate({
      flagId,
      scopeType: "user",
      scopeId: userId,
    });
    
    // Clear search term and results
    setUserSearchTerms(prev => ({ ...prev, [flagId]: "" }));
    setSearchResults(prev => ({ ...prev, [flagId]: [] }));
  };

  const handleRemoveScope = (flagId: number, scopeType: string, scopeId: string) => {
    deleteScopeMutation.mutate({ flagId, scopeType, scopeId });
  };

  if (flagsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (flagsError) {
    return (
      <div className="text-red-500 text-center py-8">
        Error loading feature flags: {flagsError instanceof Error ? flagsError.message : 'Unknown error'}
      </div>
    );
  }

  return (
    <div className="space-y-6 container mx-auto max-w-screen-lg">
      {/* Stats Card */}
      <div className="flex justify-center">
        <Card className="w-[300px]">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Feature Flags</p>
                              <p className="text-2xl font-bold">{flagsData?.featureFlags.length ?? 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Feature Flags</h2>
      </div>

      {/* Create New Flag Form */}
      {showNewFlagForm && (
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Slug</label>
                  <Input
                    placeholder="feature-slug"
                    value={newFlagSlug}
                    onChange={(e) => setNewFlagSlug(e.target.value)}
                    className="focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:transition-none bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <Input
                    placeholder="Feature description"
                    value={newFlagDescription}
                    onChange={(e) => setNewFlagDescription(e.target.value)}
                    className="focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:transition-none bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground transition-all duration-200"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleCreateFlag}
                  disabled={!newFlagSlug.trim() || createFlagMutation.isPending}
                >
                  {createFlagMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Create Flag
                </Button>
                <Button variant="outline" onClick={() => setShowNewFlagForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Feature Flags Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Slug</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Global</TableHead>
              <TableHead>Users</TableHead>
              <TableHead>Created Date</TableHead>
              <TableHead>Created By</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {flagsData?.featureFlags.map((flag) => (
              <TableRow key={flag.id}>
                <TableCell className="font-medium">{flag.slug}</TableCell>
                <TableCell>{flag.description || '-'}</TableCell>
                
                {/* Global Cell */}
                <TableCell>
                  <Switch
                    checked={hasGlobalScope(flag)}
                    onCheckedChange={() => handleToggleGlobal(flag)}
                  />
                </TableCell>

                {/* Users Cell */}
                <TableCell>
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-1">
                      {getScopesOfType(flag, "user").map((scope) => {
                        const user = getUserById(scope.scopeId);
                        return (
                          <Badge key={scope.scopeId} variant="secondary" className="text-xs">
                            {user?.username || `User ${scope.scopeId}`}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0 ml-1"
                              onClick={() => handleRemoveScope(flag.id, "user", scope.scopeId)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </Badge>
                        );
                      })}
                    </div>
                    {/* Edit Users Modal */}
                    <Dialog
                      onOpenChange={(open) => {
                        if (!open) {
                          setUserSearchTerms((prev) => ({ ...prev, [flag.id]: "" }));
                          setSearchResults((prev) => ({ ...prev, [flag.id]: [] }));
                        }
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Pencil className="h-4 w-4 mr-1" /> Edit
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[500px]">
                        <DialogHeader>
                          <DialogTitle>Edit users for {flag.slug}</DialogTitle>
                          <DialogDescription>
                            Search for users to add to this feature flag.
                          </DialogDescription>
                        </DialogHeader>
                        <Input
                          placeholder="Search users..."
                          value={userSearchTerms[flag.id] || ""}
                          onChange={(e) =>
                            setUserSearchTerms((prev) => ({
                              ...prev,
                              [flag.id]: e.target.value,
                            }))
                          }
                          className="focus-visible:ring-0 focus-visible:ring-offset-0 bg-background border border-input rounded-lg"
                        />
                        {userSearchTerms[flag.id] && (
                          <ScrollArea className="max-h-40 rounded-md border border-border mt-2">
                            {searchLoading[flag.id] ? (
                              <div className="p-2 text-sm text-muted-foreground flex items-center">
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Searching...
                              </div>
                            ) : (
                              <>
                                {searchResults[flag.id]?.map((user) => (
                                  <div
                                    key={user.id}
                                    className="p-2 hover:bg-accent hover:text-accent-foreground cursor-pointer text-sm"
                                    onClick={() => handleAddUserScope(flag.id, user.id.toString())}
                                  >
                                    <div className="font-medium">{user.username}</div>
                                    <div className="text-xs text-muted-foreground">{user.email}</div>
                                  </div>
                                ))}
                                {searchResults[flag.id]?.length === 0 && !searchLoading[flag.id] && (
                                  <div className="p-2 text-sm text-muted-foreground">
                                    No users found
                                  </div>
                                )}
                              </>
                            )}
                          </ScrollArea>
                        )}
                      </DialogContent>
                    </Dialog>
                  </div>
                </TableCell>

                {/* Created Date */}
                <TableCell>{new Date(flag.createdAt).toLocaleDateString()}</TableCell>
                
                {/* Created By */}
                <TableCell>{flag.createdBy || '-'}</TableCell>

              </TableRow>
            ))}
            {flagsData?.featureFlags.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No feature flags found. Create one to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
} 